import { Dialog, DialogActions } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import { getCompanyMultipleConfig } from '../../../../../../../lib/api/company';
import {
  createFactoringExtension,
  getAvailableDocumentsForFactoringExtension,
  getDocumentWithExtensionData,
} from '../../../../../../../lib/api/extension/factoring';
import CustomButton from '../../../../../../../lib/designSystem/Button';
import CenteredCircularProgress from '../../../../../../../lib/designSystem/CircularProgress';
import {
  formatDate,
  showAlert,
  validatePermissions,
} from '../../../../../../../lib/functions/utils';
import MultiExtensionDocumentSelector from './components/DocumentSelector';
import ExtensionsTable from './components/ExtensionsTable';
import ExtensionSummary from './components/ExtensionSummary';
import { ExtensionDocument, MultiExtensionMainProps } from './types';

const MultiExtensionMain = (props: MultiExtensionMainProps) => {
  const { open, setOpen, setUp, pagination } = props;
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<any>([]);
  const [documents, setDocuments] = useState<string[]>([]);
  const [extensions, setExtensions] = useState<ExtensionDocument[]>([]);
  const [openDocumentSelector, setOpenDocumentSelector] = useState(false);
  const [fixedArrearRate, setFixedArrearRate] = useState<number | null>(null);
  const [noIvaComision, setNoIvaComision] = useState<number | null>(null);
  const [canChangeArrearRate, setCanChangeArrearRate] = useState(false);

  interface ValidationRule {
    condition: (extension: ExtensionDocument) => boolean;
    message: (folio: string) => string;
  }

  const validParams = () => {
    const validations: ValidationRule[] = [
      {
        condition: (extension: ExtensionDocument) => !extension.dueDate,
        message: (folio: string) =>
          `El documento con folio ${folio} no tiene una fecha de vencimiento`,
      },
      {
        condition: (extension: ExtensionDocument) =>
          !extension.rate || extension.rate === '0',
        message: (folio: string) =>
          `El documento con folio ${folio} no tiene una tasa válida`,
      },
      {
        condition: (extension: ExtensionDocument) => extension.days <= 0,
        message: (folio: string) =>
          `El documento con folio ${folio} tiene días inválidos`,
      },
    ];

    for (const extension of extensions) {
      for (const validation of validations) {
        if (validation.condition(extension)) {
          showAlert(dispatch, 'error', validation.message(extension.folio));
          return false;
        }
      }
    }
    return true;
  };

  const getDocumentsData = async (
    documentIds: string[],
  ): Promise<ExtensionDocument[]> => {
    try {
      const promises = documentIds.map(async (documentId) => {
        const response = await getDocumentWithExtensionData(
          dispatch,
          documentId,
        );
        const days = calculateDays(
          new Date(response.startingPaymentDate),
          new Date(response.paymentDate),
        );

        const rateCost = fixedArrearRate
          ? (response.documentRemainingAmountNet * fixedArrearRate * days) /
            30 /
            100
          : 0;

        return {
          id: response.id,
          folio: response.folio,
          businessIdentifier: response.businessIdentifier,
          businessName: response.businessName,
          stakeholderIdentifier: response.stakeholderIdentifier,
          stakeholderName: response.stakeholderName,
          paymentDate: response.paymentDate,
          startingPaymentDate: response.startingPaymentDate,
          documentRemainingAmountNet: response.documentRemainingAmountNet,
          amount: response.amount,
          amountConciliated: response.amountConciliated,
          totalRemaining: response.totalRemaining,
          totalArrear: response.totalArrear || 0,
          dueDate: formatDate(new Date(response.paymentDate)),
          rate: fixedArrearRate ? fixedArrearRate.toString() : '',
          operationCostFixed: 0,
          operationCostVariable: 0,
          operationCostRateVariable: '',
          rateCost,
          observation: '',
          days,
        };
      });

      return await Promise.all(promises);
    } catch (error) {
      console.log(error);
      return [];
    }
  };

  const calculateDays = (start: Date, end: Date) => {
    const days = Math.floor(
      (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
    );
    return days;
  };

  const handleSearchData = async () => {
    setLoading(true);
    setOpenDocumentSelector(false);
    try {
      const newExtensions: ExtensionDocument[] =
        await getDocumentsData(documents);
      const oldExtensions: ExtensionDocument[] = [...extensions];
      setExtensions([...oldExtensions, ...newExtensions]);
      setDocuments([]);
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!validParams()) {
      return;
    }

    if (!extensions.length) {
      showAlert(
        dispatch,
        'error',
        'No se registraron prórrogas. Si es un error, te pedimos que las registres manualmente.',
      );
      return;
    }

    setLoading(true);
    try {
      const batchData = {
        extensions: extensions.map((extension) => ({
          documentId: extension.id,
          rate: parseFloat(extension.rate),
          rateCost: extension.rateCost,
          operationCostRateVariable:
            parseFloat(extension.operationCostRateVariable) || 0,
          startDate: new Date(extension.paymentDate),
          operationCostFixed: Number(extension.operationCostFixed) || 0,
          operationCostVariable: Number(extension.operationCostVariable) || 0,
          dueDate: new Date(extension.dueDate),
          observation: extension.observation || '',
        })),
      };

      const results = await createFactoringExtension(dispatch, batchData);

      const errors = results.filter((result: any) => !result.success);
      if (errors.length > 0) {
        const documentIdToFolio = extensions.reduce(
          (acc: any, extension: any) => {
            acc[extension.id] = extension.folio;
            return acc;
          },
          {},
        );

        const errorMessages = errors
          .map((error: any) => {
            const folio =
              documentIdToFolio[error.documentId] || error.documentId;
            return `Folio ${folio}`;
          })
          .join('\n');
        showAlert(
          dispatch,
          'error',
          `Errores al crear prórrogas:\n${errorMessages}`,
        );
      } else {
        setExtensions([]);
        showAlert(dispatch, 'success', 'Prórrogas creadas correctamente');
        setOpen(false);
        setUp({ page: pagination.page, pageSize: pagination.pageSize });
      }
    } catch (error: any) {
      showAlert(dispatch, 'error', 'Error al crear las prórrogas');
    }
    setLoading(false);
  };

  const setUpSuggestions = async () => {
    try {
      const response =
        await getAvailableDocumentsForFactoringExtension(dispatch);
      setSuggestions(response.documentsProcessed);
    } catch (error) {
      console.log(error);
    }
  };

  const setUpConfig = async () => {
    try {
      const response = await getCompanyMultipleConfig(dispatch, [
        'fixed-arrear-rate-factoringExtension',
        'factoringExtension-no-iva-comision',
      ]);
      const fixedArrearRate = response['fixed-arrear-rate-factoringExtension']
        ? parseFloat(response['fixed-arrear-rate-factoringExtension'])
        : null;
      const noIvaComision = response['factoringExtension-no-iva-comision']
        ? parseFloat(response['factoringExtension-no-iva-comision'])
        : null;

      setNoIvaComision(noIvaComision);
      setFixedArrearRate(fixedArrearRate);

      const canChangeArrearRate = validatePermissions(
        user,
        'factoring_request_edit_arrear_rate',
      );
      setCanChangeArrearRate(
        fixedArrearRate !== null ? canChangeArrearRate : true,
      );
    } catch (error) {
      console.log(error);
    }
  };

  const setUpDialog = async () => {
    setLoading(true);
    await setUpSuggestions();
    await setUpConfig();
    setLoading(false);
  };

  useEffect(() => {
    if (open) {
      setUpDialog();
    }
  }, [open, user]);

  const suggestionsAvailables = suggestions.filter(
    (suggestion: any) =>
      !extensions.some(
        (extension: ExtensionDocument) => extension.id === suggestion.id,
      ),
  );

  return (
    <Dialog maxWidth="xl" fullWidth open={open}>
      <div style={{ padding: 20, minHeight: 200 }}>
        {loading ? (
          <CenteredCircularProgress type="dialog" />
        ) : (
          <div>
            <ExtensionSummary
              extensions={extensions}
              setOpenDocumentSelector={setOpenDocumentSelector}
              noIvaComision={noIvaComision}
            />

            <div style={{ padding: '10px 0px' }}>
              <ExtensionsTable
                extensions={extensions}
                setExtensions={setExtensions}
                fixedArrearRate={fixedArrearRate}
                noIvaComision={noIvaComision}
                canChangeArrearRate={canChangeArrearRate}
              />
            </div>

            <DialogActions>
              <CustomButton onClick={() => setOpen(false)} color="secondary">
                Salir
              </CustomButton>
              <CustomButton
                onClick={() => handleSubmit()}
                color="primary"
                disabled={extensions.length === 0}
              >
                Confirmar
              </CustomButton>
            </DialogActions>
          </div>
        )}
      </div>

      {openDocumentSelector && (
        <MultiExtensionDocumentSelector
          suggestions={suggestionsAvailables}
          documents={documents}
          setDocuments={setDocuments}
          handleConfirm={handleSearchData}
          open={openDocumentSelector}
          setOpen={setOpenDocumentSelector}
        />
      )}
    </Dialog>
  );
};

export default MultiExtensionMain;
